import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { BaseEntityService } from 'src/app/shared/services/base-entity.service';
import { environment } from 'src/environments/environment';
import { ISchedulerSettingsService } from '../interfaces/scheduler-settings-service.interface';
import { ISchedulerSettings } from '../interfaces/scheduler-settings.interface';
import { SchedulerSettingsMapper } from '../mappers/scheduler-settings.mapper';
import { SchedulerSettingsFactory } from '../models/scheduler-settings';
import Response = ZoningAPI.Response;

const API_ENDPOINT = environment.ZONAR_API_URL + '/scheduler/settings';

@Injectable({
    providedIn: 'root'
})
export class SchedulerSettingsService extends BaseEntityService<ISchedulerSettings>
    implements ISchedulerSettingsService {
    private readonly schedulerSettingsSubject = new BehaviorSubject<ISchedulerSettings>(null);
    readonly schedulerSettings = this.schedulerSettingsSubject.asObservable().pipe(distinctUntilChanged());
    private mapper = new SchedulerSettingsMapper();

    constructor(protected readonly http: HttpClient, protected readonly authService: AuthenticationService) {
        super(SchedulerSettingsFactory);
        this.requestSchedulerSettings().subscribe(
            _schedulerSettings => {},
            err => this.schedulerSettingsSubject.error(err)
        );
    }

    requestSchedulerSettings(): Observable<ISchedulerSettings> {
        return this.http.get<Response>(API_ENDPOINT).pipe(
            map(response => this.initIfDataExists(this.mapper.fromDTO(response.data))),
            map(schedulerSettings => {
                this.schedulerSettingsSubject.next(schedulerSettings);
                return schedulerSettings;
            })
        );
    }

    updateSchedulerSettings(schedulerSettings: ISchedulerSettings): Observable<void> {
        if (!schedulerSettings) {
            return of(null);
        }
        return this.http.post<Response>(API_ENDPOINT, this.mapper.toDTO(schedulerSettings)).pipe(
            map(response => this.initIfDataExists(this.mapper.fromDTO(response.data))),
            map(schedulerSettings => this.schedulerSettingsSubject.next(schedulerSettings))
        );
    }
}

export const schedulerSettingsServiceFactory = (
    http: HttpClient,
    authService: AuthenticationService
): ISchedulerSettingsService => {
    return new SchedulerSettingsService(http, authService);
};
